import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["for"]

import { ref, computed } from "vue";
import VueSelect, { type Option } from "vue3-select-component";
import { debounce, snakeCase } from "lodash-es";

interface Props {
  label?: string;
  modelValue?: Option<number> | null;
  disabled?: boolean;
  searchPathIdentifier:
    | "games"
    | "series"
    | "platforms"
    | "genres"
    | "engines"
    | "companies"
    | "stores";
  grandparentClass?: string;
  parentClass?: string;
  placeholder?: string;
  customOptionFunc?: (item: { id: number; name: string }) => Option<number>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'single-select',
  props: {
    label: {},
    modelValue: {},
    disabled: { type: Boolean, default: false },
    searchPathIdentifier: {},
    grandparentClass: { default: "field" },
    parentClass: { default: "control" },
    placeholder: {},
    customOptionFunc: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Reactive data
// Store search results separately from pre-selected value
const searchOptions = ref<Option<number>[]>([]);
const searchPath = computed(
  () => `${window.location.origin}/${props.searchPathIdentifier}/search.json`
);
const isLoading = ref(false);

// Combine pre-selected value with search results for the options list
// This ensures the selected value can always be looked up by the component
const allOptions = computed(() => {
  const selected = props.modelValue;
  const searched = searchOptions.value;
  if (!selected) return searched;
  // Include selected option if not already in search results
  if (searched.some((opt) => opt.value === selected.value)) {
    return searched;
  }
  return [selected, ...searched];
});

// Extract just the value for modelValue (vue3-select-component expects value, not Option)
const selectedValue = computed(() => props.modelValue?.value ?? null);

const defaultOptionFunc = (item: { id: number; name: string }): Option<number> => ({
  label: item.name,
  value: item.id
});

// When value changes, look up full Option object and emit
function handleValueChange(value: number | number[] | null | undefined) {
  // vue3-select-component can emit array for some edge cases, handle both
  const singleValue = Array.isArray(value) ? (value[0] ?? null) : (value ?? null);
  if (singleValue === null) {
    emit("update:modelValue", null);
    return;
  }
  const selectedOption = allOptions.value.find((opt) => opt.value === singleValue);
  emit("update:modelValue", selectedOption ?? null);
}

/*
 * @param {search} String Current search text
 */
const onSearch = debounce(async (search: string) => {
  isLoading.value = true;
  const searchUrl = new URL(searchPath.value);
  searchUrl.searchParams.append("query", search);
  try {
    const response = await fetch(searchUrl.toString(), {
      headers: {
        "Content-Type": "application/json"
      }
    });

    const data: { id: number; name: string }[] = await response.json();
    const optionFunc = props.customOptionFunc ?? defaultOptionFunc;
    searchOptions.value = data.map(optionFunc);
  } catch (e) {
    console.error(`Error searching ${props.searchPathIdentifier}:`, e);
  } finally {
    isLoading.value = false;
  }
}, 250);

// Computed properties
const inputId = computed(() => {
  return snakeCase(props.label);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(__props.grandparentClass)
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId.value,
          class: "label"
        }, _toDisplayString(__props.label), 9, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(__props.parentClass)
    }, [
      _createVNode(_unref(VueSelect), {
        options: allOptions.value as any,
        isDisabled: __props.disabled,
        onSearch: _unref(onSearch),
        inputId: __props.label ? inputId.value : undefined,
        placeholder: __props.placeholder,
        isClearable: true,
        isLoading: isLoading.value,
        modelValue: selectedValue.value,
        "onUpdate:modelValue": handleValueChange
      }, null, 8, ["options", "isDisabled", "onSearch", "inputId", "placeholder", "isLoading", "modelValue"])
    ], 2)
  ], 2))
}
}

})